// packages/web-ui/src/components/welcome/RecentList.tsx
//
// W2-S10 Task 2.3 (AC #2) — split rail rendering the 5 most-recent chat sessions
// (left) and 5 most-recent files (right) inside the empty-welcome surface.
//
// Inputs are passed in as plain arrays so this component stays decoupled from
// the slice/API. The host (`<EmptyWelcome>`) wires zustand reads + the
// `GET /api/v1/files/recent` fetch and threads the results in. This keeps
// the file pure-presentation and trivially testable.

import type { JSX } from 'react';

export interface RecentSession {
  sessionId: string;
  title: string;
  /** ISO timestamp shown as the row's secondary text. */
  updatedAt: string;
}

export interface RecentFile {
  /** Workspace-relative path. */
  path: string;
  /** ISO timestamp shown as the row's secondary text. */
  modifiedAt: string;
}

export interface RecentListProps {
  sessions: ReadonlyArray<RecentSession>;
  files: ReadonlyArray<RecentFile>;
  /** Loading state for the files fetch (sessions are sync from the slice). */
  filesStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** Callback when a session row is activated. */
  onActivateSession: (sessionId: string) => void;
  /** Callback when a file row is activated. */
  onActivateFile: (path: string) => void;
  /** Optional test-id override. */
  testId?: string;
}

const MAX_ROWS = 5;

export function RecentList({
  sessions,
  files,
  filesStatus,
  onActivateSession,
  onActivateFile,
  testId = 'welcome-recent',
}: RecentListProps): JSX.Element {
  const trimmedSessions = sessions.slice(0, MAX_ROWS);
  const trimmedFiles = files.slice(0, MAX_ROWS);

  return (
    <section
      data-testid={testId}
      aria-label="Recent activity"
      className="grid grid-cols-1 gap-4 md:grid-cols-2"
    >
      <div data-testid={`${testId}-sessions`}>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-taupe-700">
          Recent chats
        </h3>
        {trimmedSessions.length === 0 ? (
          <p
            data-testid={`${testId}-sessions-empty`}
            className="text-xs italic text-taupe-700"
          >
            No conversations yet.
          </p>
        ) : (
          <ul role="list" className="flex flex-col gap-1">
            {trimmedSessions.map((s) => (
              <li key={s.sessionId}>
                <button
                  type="button"
                  data-testid={`${testId}-session-${s.sessionId}`}
                  onClick={() => onActivateSession(s.sessionId)}
                  className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-cream-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-taupe-700"
                >
                  <span className="font-medium text-taupe-900">{s.title}</span>
                  <span className="ml-2 text-taupe-700">
                    <time dateTime={s.updatedAt}>{shortDate(s.updatedAt)}</time>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid={`${testId}-files`}>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-taupe-700">
          Recent files
        </h3>
        {filesStatus === 'loading' ? (
          <p
            data-testid={`${testId}-files-loading`}
            className="text-xs italic text-taupe-700"
          >
            Loading recent files…
          </p>
        ) : filesStatus === 'error' ? (
          // Soft-fail: empty placeholder, not a hard error (AC #2).
          <p
            data-testid={`${testId}-files-error`}
            className="text-xs italic text-taupe-700"
          >
            Recent files unavailable.
          </p>
        ) : trimmedFiles.length === 0 ? (
          <p
            data-testid={`${testId}-files-empty`}
            className="text-xs italic text-taupe-700"
          >
            No recent files.
          </p>
        ) : (
          <ul role="list" className="flex flex-col gap-1">
            {trimmedFiles.map((f) => (
              <li key={f.path}>
                <button
                  type="button"
                  data-testid={`${testId}-file-${encodeURIComponent(f.path)}`}
                  onClick={() => onActivateFile(f.path)}
                  className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-cream-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-taupe-700"
                >
                  <span className="font-medium text-taupe-900">{baseName(f.path)}</span>
                  <span className="ml-2 text-taupe-700">{f.path}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function baseName(p: string): string {
  const parts = p.split('/');
  return parts[parts.length - 1] ?? p;
}

function shortDate(iso: string): string {
  // Defensive: invalid ISO falls back to the raw string (we never throw).
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}
